"use client";
import BodyLineText from "@/components/common/text/bodyline-text";
import HeadlineText from "@/components/common/text/headline-text";
import ScreenLayout from "@/components/layout/screen-layout";
import React from "react";

const values = [
  {
    icon: (
      <svg
        className="w-8 h-8"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
    ),
    title: "Innovation",
    description:
      "We constantly push boundaries, exploring new technologies and methodologies to deliver cutting-edge solutions that give our clients a competitive advantage.",
    color: "#FF6B6B", // Red-orange
  },
  {
    icon: (
      <svg
        className="w-8 h-8"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    title: "Excellence",
    description:
      "Quality is not just a goal, it&apos;s our standard. Every line of code, every design element, and every user interaction is crafted with meticulous attention to detail.",
    color: "#4ECDC4", // Teal
  },
  {
    icon: (
      <svg
        className="w-8 h-8"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
    title: "Collaboration",
    description:
      "We believe in the power of teamwork. Our collaborative approach ensures that diverse perspectives and expertise come together to create exceptional results.",
    color: "#45B7D1", // Blue
  },
  {
    icon: (
      <svg
        className="w-8 h-8"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
    ),
    title: "Integrity",
    description:
      "Trust is the foundation of every relationship. We operate with complete transparency, honesty, and ethical practices in all our business dealings.",
    color: "#A855F7", // Purple
  },
  {
    icon: (
      <svg
        className="w-8 h-8"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
    title: "Learning",
    description:
      "The tech landscape evolves rapidly. We embrace continuous learning, staying ahead of trends and constantly improving our skills and knowledge.",
    color: "#F59E0B", // Amber
  },
  {
    icon: (
      <svg
        className="w-8 h-8"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v2a2 2 0 01-2 2H10a2 2 0 01-2-2V6m8 0H8m0 0v.01M8 6v.01"
        />
      </svg>
    ),
    title: "Impact",
    description:
      "We measure our success by the positive impact we create for our clients, their users, and the broader community through technology solutions.",
    color: "#10B981", // Emerald
  },
];

function OurValues() {
  return (
    <div className="md:pb-17.5 pb-9 bg-grey-section/30">
      <ScreenLayout>
        {/* Section Header */}
        <div className="text-center mb-16 flex flex-col items-center justify-center">
          <HeadlineText>The Principles That Guide Us</HeadlineText>
          <BodyLineText>
            Our core values shape everything we do, from how we approach
            projects to how we interact with our team and clients
          </BodyLineText>
        </div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {values.map((value) => (
            <div key={value.title} className="group">
              <div className="relative bg-white rounded-3xl p-8 border border-gray-100 h-full transition-all duration-300 ease-out group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.06)] group-hover:border-gray-200 hover:-translate-y-1">
                {/* Modern gradient overlay */}
                <div
                  className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(135deg, ${value.color}03 0%, transparent 50%)`,
                  }}
                />

                {/* Content */}
                <div className="relative z-10">
                  {/* Clean Modern Icon */}
                  <div className="mb-8">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 ease-out border border-gray-100"
                      style={{
                        background: `linear-gradient(135deg, ${value.color}08 0%, ${value.color}04 100%)`,
                        color: value.color,
                      }}
                    >
                      <div className="w-7 h-7">
                        {React.cloneElement(value.icon, {
                          className: "w-7 h-7",
                          strokeWidth: 1.5,
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors duration-300">
                    {value.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed text-[15px] group-hover:text-gray-700 transition-colors duration-300">
                    {value.description}
                  </p>
                </div>

                {/* Clean accent line */}
                <div
                  className="absolute bottom-0 left-8 right-8 h-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-x-0 group-hover:scale-x-100"
                  style={{
                    background: `linear-gradient(90deg, ${value.color}40 0%, ${value.color}10 50%, transparent 100%)`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </ScreenLayout>
    </div>
  );
}

export default OurValues;
